import { useContext } from 'react'
import { useIntl } from 'react-intl'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import {
  INTL_DMY_CASUAL_FORMAT,
  INTL_DAY_MONTH_NAME,
} from 'constants/dateFormats'
import GeoContext from 'contexts/geoContext'
import useTourV2SnapshotDetails from 'hooks/TourV2/useTourV2SnapshotDetails'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { formatDynamicAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import { extractOfferIdsFromOrder } from 'tripPlanner/components/EntryPoints/utils'
import BookingItemCard from 'tripPlanner/components/ItemCard/BookingItemCard'
import type { OfferTypeForTripItemTypes } from 'tripPlanner/hooks/api/common'
import { concatStringWithDot } from 'tripPlanner/utils'
import useOffers from 'hooks/Offers/useOffers'
import useTourV2Snapshot from 'hooks/TourV2/useTourV2Snapshot'

type Props = {
  order: App.Order
  tourV2OrderItem: App.Tours.TourV2OrderItem
  onSelect?: (
    order: App.Order,
    offers: Array<OfferTypeForTripItemTypes>,
    checked: boolean,
  ) => void
}

function TourV2BookingCard(props: Props) {
  const { tourV2OrderItem, onSelect, order } =
    props
  const intl = useIntl()
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const intlDateFormatter = useIntlDateFormatter()
  const [offers, isOffersLoading] = useOffers(extractOfferIdsFromOrder(order))
  const [snapshot, isSnapshotLoading] = useTourV2Snapshot(
    tourV2OrderItem.reservationId,
    tourV2OrderItem.orderId,
  )

  const { formattedEndDate, formattedStartDate } =
    useTourV2SnapshotDetails(snapshot)

  const renderStartAndEndDate = () => {
    const startDate = formattedStartDate ?
        intlDateFormatter(formattedStartDate, INTL_DAY_MONTH_NAME) :
      ''
    const endDate = formattedEndDate ?
        intlDateFormatter(formattedEndDate, INTL_DMY_CASUAL_FORMAT) :
      ''

    if (!startDate) {
      return endDate
    }

    if (!endDate) {
      return startDate
    }

    return intl.formatMessage(
      {
        id: 'q27BIBzioAiyBNIiBFT1',
        defaultMessage: '{startDate} - {endDate}',
        description: 'Tour booking card date range showing the start and end dates of the booked tour',
      },
      {
        startDate,
        endDate,
      },
    )
  }

  const isLoading = isOffersLoading || isSnapshotLoading

  if (isLoading) {
    return <LoadingIndicator inline />
  }

  return (
    <BookingItemCard
      heading={tourV2OrderItem.tour.tourOptionTitle}
      image={{ id: snapshot?.imageId }}
      subHeading={concatStringWithDot(
        renderStartAndEndDate(),
        formatDynamicAmountWithDollarType(
          tourV2OrderItem.total,
          currentCurrency,
          currentRegionCode,
        ),
      )}
      onSelect={(checked) => {
        if (onSelect) {
          onSelect(order, offers, checked)
        }
      }}
    />
  )
}

export default TourV2BookingCard
